<template>
  <div class="min-h-screen flex flex-col bg-background text-text">
    <!-- Header -->
    <header class="bg-background-light border-b border-background-lighter relative z-50">
      <div class="container mx-auto px-4 py-3">
        <div class="flex items-center justify-between w-full">
          <!-- Left side: Menu toggle + Logo -->
          <div class="flex items-center">
            <button
              @click="toggleMobileMenu"
              class="p-2 mr-3 rounded-md hover:bg-background-lighter transition-colors lg:hidden"
              aria-label="Toggle menu"
            >
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"></path>
              </svg>
            </button>

            <router-link to="/" class="flex items-center">
              <img
                src="/logo_small.png"
                alt="Eskrag"
                class="h-10 w-10 mr-3"
                @error="handleImageError"
              />
              <span class="text-primary text-2xl font-bold">Eskrag</span>
            </router-link>
          </div>

          <!-- Search and actions -->
          <div class="flex items-center space-x-4">
            <div class="relative hidden md:block">
              <input
                type="text"
                placeholder="Search products..."
                class="w-64 bg-background border border-background-lighter rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                v-model="searchQuery"
                @keyup.enter="handleSearch"
              >
              <button
                class="absolute right-3 top-2.5 text-text-secondary"
                @click="handleSearch"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clip-rule="evenodd" />
                </svg>
              </button>
            </div>

            <NotificationPanel />

            <!-- Cart Button -->
            <button
              v-if="!dataStore.shouldHideCart"
              @click="cartStore.toggleCart"
              class="relative p-2 text-text-secondary hover:text-text hover:bg-background-lighter rounded-full transition-colors"
              aria-label="Shopping Cart"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <span
                v-if="cartStore.count > 0"
                class="absolute -top-1 -right-1 bg-primary text-white rounded-full text-xs w-5 h-5 flex items-center justify-center"
              >
                {{ cartStore.count }}
              </span>
            </button>

            <!-- User actions -->
            <div class="flex items-center">
              <div class="relative user-menu">
                <button
                  @click="showUserMenu = !showUserMenu"
                  class="flex items-center space-x-2 bg-background-light hover:bg-background-lighter px-3 py-2 rounded-md"
                >
                  <span class="text-sm hidden md:inline">{{ authStore.currentUser }}</span>
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-text-secondary" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clip-rule="evenodd" />
                  </svg>
                </button>

                <!-- Dropdown menu -->
                <div
                  v-if="showUserMenu"
                  class="absolute right-0 mt-2 w-56 bg-background border border-background-lighter rounded-md shadow-2xl z-[100]"
                >
                  <div class="py-1">
                    <div class="px-4 py-2 text-xs text-text-secondary font-medium uppercase tracking-wider">
                      {{ authStore.currentUser }}
                    </div>

                    <router-link
                      to="/panel/profile"
                      class="flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-background-lighter text-text-primary transition-colors"
                      @click="showUserMenu = false"
                    >
                      <span>👤 Profile</span>
                    </router-link>

                    <theme-toggle menu-item />

                    <div class="border-t border-background-lighter my-1"></div>

                    <button @click="handleLogout" class="flex items-center gap-3 w-full text-left px-4 py-2.5 text-sm hover:bg-background-lighter text-red-500 transition-colors">
                      Logout
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>

    <!-- Main Layout Container -->
    <div class="flex flex-1 overflow-hidden">
      <!-- Mobile Backdrop -->
      <div
        v-if="isMobileMenuOpen"
        class="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden"
        @click="closeMobileMenu"
      ></div>

      <!-- Sidebar -->
      <div
        :class="[
          'fixed inset-y-0 left-0 z-50 w-64 bg-background-light shadow-lg transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 flex flex-col lg:z-auto',
          isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        ]"
      >
        <!-- User Info -->
        <div class="p-4 border-b border-background-lighter flex-shrink-0">
          <div class="flex items-center space-x-3">
            <div class="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
              <span class="text-white font-semibold">{{ userInitials }}</span>
            </div>
            <div>
              <p class="font-medium text-text-primary">{{ user?.name || 'User' }}</p>
              <p class="text-sm text-text-secondary">{{ user?.email || 'user@example.com' }}</p>
            </div>
          </div>
        </div>

        <!-- Navigation Menu -->
        <nav class="flex-1 p-4 space-y-1 overflow-y-auto">
          <template v-for="item in availableMenuItems" :key="item.name">
            <router-link
              :to="item.path"
              :class="[
                'flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200',
                $route.path === item.path
                  ? 'bg-primary text-white'
                  : 'text-text-secondary hover:bg-background-lighter hover:text-text-primary'
              ]"
              @click="closeMobileMenu"
            >
              <component :is="item.icon" class="w-5 h-5 mr-3" />
              {{ item.name }}
            </router-link>
          </template>
        </nav>
      </div>

      <!-- Main Content Area -->
      <div class="flex-1 flex flex-col overflow-hidden">
        <main class="flex-1 overflow-auto bg-background">
          <div class="p-4 lg:p-6">
            <router-view />
          </div>
        </main>
      </div>
    </div>

    <Cart v-if="!dataStore.shouldHideCart" />
  </div>
</template>

<script>
import { ref, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAuthStore } from '../stores/auth';
import { useDataStore } from '../stores/dataStore';
import { useCartStore } from '../stores/cartStore';
import NotificationPanel from '../components/notifications/NotificationPanel.vue';
import ThemeToggle from '../components/ui/ThemeToggle.vue';
import Cart from '../components/Cart.vue';

import {
  UserIcon,
  ClockIcon,
  ShoppingBagIcon,
  CreditCardIcon,
  WalletIcon,
  BellIcon
} from '@heroicons/vue/24/outline';

export default {
  name: 'UserPanelLayout',
  components: {
    NotificationPanel,
    ThemeToggle,
    Cart,
    UserIcon,
    ClockIcon,
    ShoppingBagIcon,
    CreditCardIcon,
    WalletIcon,
    BellIcon
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const authStore = useAuthStore();
    const dataStore = useDataStore();
    const cartStore = useCartStore();

    const isMobileMenuOpen = ref(false);
    const searchQuery = ref('');
    const showUserMenu = ref(false);

    const allMenuItems = [
      { name: 'Profile', path: '/panel/profile', icon: 'UserIcon' },
      { name: 'Digital Orders', path: '/panel/orders', icon: 'ShoppingBagIcon' },
      { name: 'Physical Orders', path: '/panel/physical-orders', icon: 'ShoppingBagIcon' },
      { name: 'My Device Buybacks', path: '/panel/my-buybacks', icon: 'WalletIcon' },
      { name: 'Notifications', path: '/panel/notifications', icon: 'BellIcon' },
      { name: 'Transaction History', path: '/panel/transactions', icon: 'CreditCardIcon' },
      { name: 'Login History', path: '/panel/login-history', icon: 'ClockIcon' }
    ];

    const user = computed(() => authStore.user);

    const userInitials = computed(() => {
      const name = user.value?.name || 'U';
      return name.charAt(0).toUpperCase();
    });

    const availableMenuItems = computed(() => allMenuItems);

    const toggleMobileMenu = () => {
      isMobileMenuOpen.value = !isMobileMenuOpen.value;
    };

    const closeMobileMenu = () => {
      isMobileMenuOpen.value = false;
    };

    const handleSearch = () => {
      if (searchQuery.value.trim() === '') return;
      router.push({ name: 'search', query: { q: searchQuery.value } });
      searchQuery.value = '';
    };

    const handleImageError = (event) => {
      event.target.src = '/placeholder-image.png';
    };

    const handleLogout = async () => {
      await authStore.logout();
      router.push('/login');
    };

    return {
      authStore,
      dataStore,
      cartStore,
      isMobileMenuOpen,
      searchQuery,
      showUserMenu,
      user,
      userInitials,
      availableMenuItems,
      toggleMobileMenu,
      closeMobileMenu,
      handleSearch,
      handleImageError,
      handleLogout
    };
  }
};
</script>
